import {inject, injectable} from "inversify";
import type {IHttpClientService} from "data/services/http";
import type {AxiosResponse} from "axios";
import {Bindings} from "data/constants/bindings";
import {RoundStatus, TournamentStatus} from "data/enums";
import {AbstractJsonProvider} from "./abstract.json.provider";
import type {ISquad} from "data/types/api";
import {
	IStaticContentPayload,
	ICategories,
	ISections,
	IArticles,
} from "data/stores/static_content/static_content.store";

export interface ICountry {
	name: string;
	code: string;
}

export interface ITournament {
	id: number;
	venueName: Record<string, string>;
	venueId: number;
	date: string;
	homeSquadId: number;
	awaySquadId: number;
	homeSquadName: string;
	awaySquadName: string;
	homeSquadShortName: string;
	awaySquadShortName: string;
	homeScore: null | number;
	awayScore: null | number;
	status: TournamentStatus;
	homeStats: {
		passingYards: number;
		rushingYards: number;
	};
	awayStats: {
		passingYards: number;
		rushingYards: number;
	};
	isMarginGame: boolean;
	isCBSSN: boolean;
	ctaLinks: {
		cbssn: string;
		tsn: string;
		cfl: string;
	};
}

export interface ICrunch {
	id: number;
	title: {
		en: string;
		fr: string;
	};
	video: string;
	color: string;
}

export interface IRound {
	id: number;
	status: RoundStatus;
	startDate: string;
	endDate: string;
	tournaments: ITournament[];
	isPublished: number;
	roundNumber?: number;
	crunches?: ICrunch[];
}

export interface IPlayer {
	id: number;
	first_name: string;
	last_name: string;
	squad_id: number;
}

export interface IStaticItem {
	id: number;
	title: string;
	text: string;
	game: string;
}

export interface ITips {
	[key: string]: {
		[key: string]: number;
	};
	crunches: {
		[key: string]: number;
	};
}

export interface IHelpPages {
	guidelines: IStaticItem[];
	terms: IStaticItem[];
	privacy: IStaticItem[];
	how_to_play: IStaticItem[];
	prizes: IStaticItem[];
	faq: IStaticItem[];
}

export interface IJSONProvider {
	tips(roundId: number): Promise<AxiosResponse<ITips>>;
	countries(): Promise<AxiosResponse<ICountry[]>>;
	rounds(): Promise<AxiosResponse<IRound[]>>;
	players(): Promise<AxiosResponse<IPlayer[]>>;
	squads(): Promise<AxiosResponse<ISquad[]>>;
	faq_page(): Promise<AxiosResponse<IStaticItem[]>>;

	helpCategories(params: IStaticContentPayload): Promise<AxiosResponse<ICategories>>;

	helpSections(params: IStaticContentPayload): Promise<AxiosResponse<ISections>>;

	helpArticles(params: IStaticContentPayload): Promise<AxiosResponse<IArticles>>;
}

@injectable()
export class JSONProvider extends AbstractJsonProvider implements IJSONProvider {
	constructor(
		@inject(Bindings.JsonHTTPClient) private _http: IHttpClientService,
		@inject(Bindings.ContentJsonHTTPClient) private _contentJsonClient: IHttpClientService
	) {
		super();
	}
	tips = (roundId: number) => this._http.get<ITips>(`tips/${roundId}.json`);
	countries = () => this._http.get<ICountry[]>("countries.json");
	rounds = () => this._http.get<IRound[]>("rounds.json");
	players = () => this._http.get<IPlayer[]>("players.json");
	squads = () => this._http.get<ISquad[]>("squads.json");
	faq_page = () => this._http.get<IStaticItem[]>(`${this.language}/faq.json`);
	helpCategories = ({locale, ...params}: IStaticContentPayload) =>
		this._contentJsonClient.get<ICategories>(`${locale}/categories.json`, params);
	helpSections = ({locale, categoryId, ...params}: IStaticContentPayload) =>
		this._contentJsonClient.get<ISections>(
			`${locale}/categories/${categoryId || "0"}/sections.json`,
			params
		);
	helpArticles = ({locale, categoryId, ...params}: IStaticContentPayload) =>
		this._contentJsonClient.get<IArticles>(
			`${locale}/categories/${categoryId || "0"}/articles.json`,
			params
		);
}
